import { useState, useMemo } from "react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Building2, User } from "lucide-react";
import { useContacts, useContactTags } from "@/hooks/useContacts";
import { CreateContactDialog } from "@/components/contacts/CreateContactDialog";
import { ContactDetailSheet } from "@/components/contacts/ContactDetailSheet";
import { ContactTagFilter } from "@/components/contacts/ContactTagFilter";
import {
  ContactTypeChips,
  type ContactTypeValue,
} from "@/components/contacts/ContactFilters";
import type { Contact, ContactTag } from "@/hooks/useContacts";

export default function Contacts() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<ContactTypeValue>("all");
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Debounce search input
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (debounceTimer) clearTimeout(debounceTimer);
    setDebounceTimer(setTimeout(() => setDebouncedSearch(value), 300));
  };

  const { data: contacts = [], isLoading } = useContacts(debouncedSearch, tagFilter, null, false);
  const { data: tags = [] } = useContactTags();

  const filteredContacts = useMemo(
    () =>
      typeFilter === "all"
        ? contacts
        : contacts.filter((c) => c.type === typeFilter),
    [contacts, typeFilter],
  );

  const typeCounts = useMemo(
    () => ({
      all: contacts.length,
      person: contacts.filter((c) => c.type === "person").length,
      organization: contacts.filter((c) => c.type === "organization").length,
    }),
    [contacts],
  );

  // Build tag lookup map
  const tagMap = useMemo(
    () =>
      tags.reduce<Record<string, ContactTag>>((acc, t) => {
        acc[t.tag] = t;
        return acc;
      }, {}),
    [tags]
  );

  const handleViewDetails = (contact: Contact) => {
    setSelectedContact(contact);
    setSheetOpen(true);
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-card px-6">
        <SidebarTrigger />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-foreground">Contacts</h1>
        </div>
        <CreateContactDialog />
      </header>

      {/* Main Content */}
      <div className="flex-1 p-6 space-y-4">
        {/* Filter toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-72">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search contacts..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <ContactTypeChips
            value={typeFilter}
            onChange={setTypeFilter}
            counts={typeCounts}
          />
        </div>

        {/* Tag filter bar */}
        <ContactTagFilter
          tags={tags}
          selected={tagFilter}
          onChange={setTagFilter}
        />

        {/* Contacts table */}
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : filteredContacts.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground mb-4">
              {debouncedSearch || tagFilter.length > 0 || typeFilter !== "all"
                ? "No contacts match your filters"
                : "No contacts yet"}
            </p>
            {!debouncedSearch &&
              tagFilter.length === 0 &&
              typeFilter === "all" && <CreateContactDialog />}
          </Card>
        ) : (
          <div className="border rounded-lg bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-28">Type</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead className="w-[28rem]">Tags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContacts.map((contact) => {
                  const isOrg = contact.type === "organization";
                  const Icon = isOrg ? Building2 : User;
                  const name = isOrg
                    ? contact.company || "—"
                    : [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "—";
                  return (
                    <TableRow
                      key={contact.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleViewDetails(contact)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span>{name}</span>
                          {!contact.is_active && (
                            <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0">
                              archived
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {isOrg ? "Organization" : "Person"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {isOrg ? "—" : contact.company || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {contact.role || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {contact.email || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {contact.phone || "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {contact.tags.map((slug) => {
                            const tag = tagMap[slug];
                            return (
                              <Badge
                                key={slug}
                                variant="secondary"
                                className="text-[10px] px-1.5 py-0"
                                style={
                                  tag?.color
                                    ? { backgroundColor: tag.color, color: "#fff" }
                                    : undefined
                                }
                              >
                                {tag?.label || slug}
                              </Badge>
                            );
                          })}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          {filteredContacts.length} contact{filteredContacts.length !== 1 ? "s" : ""}
          {filteredContacts.length !== contacts.length && (
            <span> of {contacts.length}</span>
          )}
        </p>
      </div>

      <ContactDetailSheet
        contact={selectedContact}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onContactUpdated={setSelectedContact}
      />
    </div>
  );
}
